'use client';
import React, { useState, useRef } from 'react';
import { Upload, Palette, Mail, Eye, Save, Check, Link, FileText, X } from 'lucide-react';

interface ContentBlock {
  id: string;
  label: string;
  enabled: boolean;
  content: string;
}

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  heading: string;
  ctaLabel: string;
  ctaUrl: string;
}

const defaultTemplates: EmailTemplate[] = [
  { id: 'assigned', name: 'Cleaner Assigned', subject: 'Cleaner Assigned — {{jobService}}', heading: 'Your Cleaner Has Been Assigned', ctaLabel: 'View Job Details', ctaUrl: '/customer-dashboard' },
  { id: 'inprogress', name: 'Job In Progress', subject: 'Cleaning In Progress — {{jobService}}', heading: 'Your Cleaning Has Started', ctaLabel: 'Track Job', ctaUrl: '/customer-dashboard' },
  { id: 'completed', name: 'Job Completed', subject: 'Job Completed — {{jobService}}', heading: 'Your Cleaning Is Complete', ctaLabel: 'Review & Approve Job →', ctaUrl: '/customer-dashboard' },
  { id: 'complaint', name: 'Complaint Filed', subject: 'Complaint Received — {{jobService}}', heading: "We've Received Your Complaint", ctaLabel: 'View Complaint Status', ctaUrl: '/customer-dashboard' },
];

const defaultBlocks: ContentBlock[] = [
  { id: 'job_details', label: 'Job Details Block', enabled: true, content: 'Shows service name, address, and scheduled date.' },
  { id: 'cleaner_info', label: 'Cleaner Info Block', enabled: true, content: 'Displays assigned cleaner name and rating.' },
  { id: 'cta_button', label: 'Call-to-Action Button', enabled: true, content: 'Primary action button linking to the dashboard.' },
  { id: 'support_note', label: 'Support Note', enabled: true, content: 'If you have any questions, please contact our support team.' },
  { id: 'social_links', label: 'Social Links Footer', enabled: false, content: 'Facebook, Instagram, and Twitter links in footer.' },
];

export default function EmailTemplatesSection() {
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [brandColor, setBrandColor] = useState('#6366f1');
  const [accentColor, setAccentColor] = useState('#10b981');
  const [blocks, setBlocks] = useState<ContentBlock[]>(defaultBlocks);
  const [templates, setTemplates] = useState<EmailTemplate[]>(defaultTemplates);
  const [activeTemplate, setActiveTemplate] = useState('assigned');
  const [saved, setSaved] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const currentTemplate = templates.find((t) => t.id === activeTemplate)!;

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const toggleBlock = (id: string) => {
    setBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, enabled: !b.enabled } : b)));
  };

  const updateBlock = (id: string, content: string) => {
    setBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, content } : b)));
  };

  const updateTemplate = (field: keyof EmailTemplate, value: string) => {
    setTemplates((prev) => prev.map((t) => (t.id === activeTemplate ? { ...t, [field]: value } : t)));
  };

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-foreground">Email Templates</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Customise branding and content for all automated Resend emails.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setPreviewOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors"
          >
            <Eye size={14} /> Preview
          </button>
          <button
            onClick={handleSave}
            className={`inline-flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold transition-all duration-150 ${
              saved ? 'bg-success text-white' : 'gradient-primary text-white hover:opacity-90 active:scale-95'
            }`}
          >
            {saved ? <><Check size={14} /> Saved!</> : <><Save size={14} /> Save Changes</>}
          </button>
        </div>
      </div>

      {/* Brand Identity */}
      <div className="bg-muted/30 rounded-2xl border border-border p-5 space-y-4">
        <div className="flex items-center gap-2 mb-1">
          <Palette size={15} className="text-primary" />
          <h3 className="text-sm font-bold text-foreground">Brand Identity</h3>
        </div>

        {/* Logo Upload */}
        <div className="flex items-center gap-4">
          <div
            className="w-20 h-20 rounded-xl border-2 border-dashed border-border bg-background flex items-center justify-center overflow-hidden cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => fileRef.current?.click()}
          >
            {logoPreview ? (
              <img src={logoPreview} alt="Email logo" className="w-full h-full object-contain p-1" />
            ) : (
              <div className="text-center">
                <Upload size={18} className="text-muted-foreground mx-auto mb-1" />
                <p className="text-xs text-muted-foreground">Upload</p>
              </div>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
          <div className="flex-1">
            <p className="text-sm font-semibold text-foreground">Email Logo</p>
            <p className="text-xs text-muted-foreground mt-0.5">Appears in the header of all outgoing emails. PNG or SVG recommended.</p>
            <button
              onClick={() => fileRef.current?.click()}
              className="mt-2 text-xs font-semibold text-primary hover:underline"
            >
              {logoPreview ? 'Change logo' : 'Upload logo'}
            </button>
            {logoPreview && (
              <button onClick={() => setLogoPreview(null)} className="ml-3 text-xs text-muted-foreground hover:text-destructive transition-colors">
                Remove
              </button>
            )}
          </div>
        </div>

        {/* Colors */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-2">Primary Brand Colour</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={brandColor}
                onChange={(e) => setBrandColor(e.target.value)}
                className="w-10 h-10 rounded-lg border border-border cursor-pointer bg-transparent p-0.5"
              />
              <input
                type="text"
                value={brandColor}
                onChange={(e) => setBrandColor(e.target.value)}
                className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Used for email header background</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-2">Accent / CTA Colour</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={accentColor}
                onChange={(e) => setAccentColor(e.target.value)}
                className="w-10 h-10 rounded-lg border border-border cursor-pointer bg-transparent p-0.5"
              />
              <input
                type="text"
                value={accentColor}
                onChange={(e) => setAccentColor(e.target.value)}
                className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Used for buttons and highlights</p>
          </div>
        </div>
      </div>

      {/* Template Selector + Editor */}
      <div className="bg-muted/30 rounded-2xl border border-border p-5 space-y-4">
        <div className="flex items-center gap-2 mb-1">
          <Mail size={15} className="text-primary" />
          <h3 className="text-sm font-bold text-foreground">Template Content</h3>
        </div>

        {/* Template tabs */}
        <div className="flex gap-1 bg-muted rounded-xl p-1 overflow-x-auto">
          {templates.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTemplate(t.id)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150 ${
                activeTemplate === t.id ? 'bg-card text-foreground card-shadow' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t.name}
            </button>
          ))}
        </div>

        {/* Template fields */}
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1.5">Email Subject</label>
            <input
              value={currentTemplate.subject}
              onChange={(e) => updateTemplate('subject', e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <p className="text-xs text-muted-foreground mt-1">Use {'{{jobService}}'} for dynamic job name</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1.5">Email Heading</label>
            <input
              value={currentTemplate.heading}
              onChange={(e) => updateTemplate('heading', e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1.5">
                <Link size={11} className="inline mr-1" />CTA Button Label
              </label>
              <input
                value={currentTemplate.ctaLabel}
                onChange={(e) => updateTemplate('ctaLabel', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1.5">
                <Link size={11} className="inline mr-1" />CTA Link Path
              </label>
              <input
                value={currentTemplate.ctaUrl}
                onChange={(e) => updateTemplate('ctaUrl', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Dynamic Content Blocks */}
      <div className="bg-muted/30 rounded-2xl border border-border p-5 space-y-3">
        <div className="flex items-center gap-2 mb-1">
          <FileText size={15} className="text-primary" />
          <h3 className="text-sm font-bold text-foreground">Dynamic Content Blocks</h3>
          <p className="text-xs text-muted-foreground ml-1">Toggle which blocks appear in all email templates</p>
        </div>

        {blocks.map((block) => (
          <div key={block.id} className={`rounded-xl border p-4 transition-all ${block.enabled ? 'border-primary/30 bg-primary/5' : 'border-border bg-background'}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-semibold text-foreground">{block.label}</p>
                  {block.enabled && (
                    <span className="text-xs font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-full">Active</span>
                  )}
                </div>
                {block.enabled && (
                  <textarea
                    value={block.content}
                    onChange={(e) => updateBlock(block.id, e.target.value)}
                    rows={2}
                    className="w-full mt-2 px-3 py-2 rounded-lg border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                  />
                )}
                {!block.enabled && (
                  <p className="text-xs text-muted-foreground">{block.content}</p>
                )}
              </div>
              <button
                onClick={() => toggleBlock(block.id)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors mt-0.5 ${block.enabled ? 'bg-primary' : 'bg-muted-foreground/30'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${block.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Preview Modal */}
      {previewOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card rounded-2xl border border-border w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-sm font-bold text-foreground">Email Preview — {currentTemplate.name}</h3>
              <button onClick={() => setPreviewOpen(false)} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
                <X size={16} className="text-muted-foreground" />
              </button>
            </div>
            <div className="p-4 bg-[#f4f4f5]">
              <div className="bg-white rounded-2xl overflow-hidden shadow-sm max-w-sm mx-auto">
                {/* Header */}
                <div style={{ background: brandColor }} className="p-6 text-center">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Logo" className="h-10 mx-auto mb-2 object-contain" />
                  ) : (
                    <p className="text-white font-extrabold text-lg">EzCleanCare</p>
                  )}
                  <p className="text-white/80 text-xs mt-1">Professional Cleaning Services</p>
                </div>
                {/* Body */}
                <div className="p-6">
                  <p className="font-bold text-gray-900 text-base mb-1">{currentTemplate.heading}</p>
                  <p className="text-gray-500 text-xs mb-4">Hi Customer,</p>
                  {blocks.find((b) => b.id === 'job_details')?.enabled && (
                    <div className="bg-gray-50 rounded-xl p-3 mb-4 text-xs text-gray-600 space-y-1">
                      <p><span className="font-semibold text-gray-400 uppercase text-[10px] tracking-wider">Service</span></p>
                      <p className="font-semibold text-gray-900">Deep Clean</p>
                      <p className="text-gray-400 text-[10px] uppercase tracking-wider mt-2">Address</p>
                      <p>123 Example Street, London</p>
                    </div>
                  )}
                  {blocks.find((b) => b.id === 'cta_button')?.enabled && (
                    <a
                      href="#"
                      style={{ background: accentColor }}
                      className="block text-center text-white text-xs font-bold py-3 rounded-lg mb-4 no-underline"
                    >
                      {currentTemplate.ctaLabel}
                    </a>
                  )}
                  {blocks.find((b) => b.id === 'support_note')?.enabled && (
                    <p className="text-gray-400 text-xs">{blocks.find((b) => b.id === 'support_note')?.content}</p>
                  )}
                </div>
                {/* Footer */}
                <div className="bg-gray-50 border-t border-gray-100 p-4 text-center">
                  <p className="text-gray-400 text-[11px]">© 2026 EzCleanCare Ltd. All rights reserved.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
